import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import markdownIt from 'markdown-it';
import { Close as DialogClose } from '@radix-ui/react-dialog';
import { Close as PopupClose } from '@radix-ui/react-popover';
import { Model, SvgRegistry } from 'survey-core';
import {
  Survey as SurveyJS,
  ReactElementFactory,
} from 'survey-react-ui';

import { addToDataLayer } from '../../../services/analytics/google';
import currentUserService from '../../../services/current_user';
import errorHandler from '../../../services/error_handler';
import { graphMutate } from '../../../requests/graphql';

import Dialog from '../DialogRadix';
import Popup from '../Popup';
import RadioGroup from './RadioGroup';
import RobotRating from './RobotRating';

import button from '../../../styles/global_ui/buttons';
import layout from '../../../styles/global_ui/layout';
import util from '../../../styles/global_ui/util';
import typography from '../../../styles/global_ui/typography';
import style from './survey.css';
import { sjsStyleVars } from './jsonStyles';

const c = {
  container: `${layout.flexColumn} ${layout.gutter10} ${style.container}`,
  pageRoot: `${layout.flexColumn} ${layout.gutter10} ${layout.marginTop5}`,
  body: `${layout.flexColumn} ${layout.gutter10}`,
  title: `${typography.h3}`,
  header: `${typography.bodyL} ${typography.charcoal} ${style.header}`,
  error: `${typography.error} ${typography.bodyM}`,
  border: `${util.border} ${util.borderRadius}`,
  input: `${util.border} ${util.borderRadius} ${style.margin1} ${style.range}`,
  radiogroup: `${layout.flexColumn} ${layout.gutter22} ${style.margin1} ${style.radioGroup}`,
  submitBtn: `${button.md}`,
  backBtn: `${button.md} ${button.transparent} ${typography.pebble}`,
  hidden: `${util.displayNone}`,
};

const baseCss = {
  body: c.pageRoot, // wraps the all questions and navigation
  header: c.title,
  page: {
    root: c.body, // wraps list of questions
    title: c.title,
  },
  question: {
    header: c.header,
    hasError: c.error,
    titleBar: c.hidden, // hide action buttons for questions. eg. "clear", "more", "..."
  },
  boolean: { item: style.boolean }, // toggle switch
  checkbox: {
    materialDecorator: style.checkbox, // input box
    itemDecorator: style.svg, // checkmark svg
    itemChecked: style.checked, // checked checkmark
    itemHover: style.hover, // hover on label
  },
  dropdown: {
    root: `${c.border} ${style.margin1} ${style.input}`, // single-select dropdown input box wrapper
    cleanButtonSvg: style.svg, // clear button svg
  },
  matrix: { cell: style.cell },
  multipletext: { content: c.input }, // input element for single line inputs
  rating: { root: style.list }, // wraps the options for ratings question
  ranking: { root: style.margin1 }, // wraps the options for ranking question
  radiogroup: { root: c.radiogroup }, // wraps the options for radio group question (allows inline options)
  tagbox: {
    root: `${c.border} ${style.input}`, // multi-select dropdown input box wrapper
    cleanButtonSvg: style.svg, // clear button svg
  },
  text: { content: `${c.input} ${style.input}` }, // input element for single line inputs
  comment: { content: style.textarea, onError: typography.charcoal },
  footer: util.posRelative, // wraps the submit prev/next buttons
  bodyNavigationButton: c.submitBtn,
  navigation: {
    complete: c.submitBtn,
    next: c.submitBtn,
    prev: c.backBtn,
    start: c.submitBtn,
  },
};

const closeIcon
  = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><g fill="currentColor" fill-rule="nonzero"><path d="M1.293 2 14 14.707l.707-.707L2 1.293z"/><path d="M2 14.707 14.707 2 14 1.293 1.293 14z"/></g></svg>';

const progressBarStyle = 'position: absolute; top: calc(50% - 2px); display: flex; justify-content: center; width: 100%; gap: 5px; z-index: -1;';

const SurveyCore = ({
  allowRepeatEntries = true,
  element,
  surveyCss,
  popup,
  onOpenChange: openChange,
  onSubmit,
  surveyJson,
  surveySlug,
  version,
}) => {
  const onOpenChange = popup?.onOpenChange || openChange;
  const closeButton = useRef();
  const pageCountRef = useRef();
  const footerRef = useRef();
  const progressBarRef = useRef();
  const [prevContent, setPrevContent] = useState();

  const fetchUserId = useCallback(
    () => currentUserService
      .getAsync('id')
      .then((id) => {
        if (!id) return;
        fetchUserSurveyResults(id);
      })
      .catch((err) => errorHandler('Survey fetchCurrentUser: ', err)),
    [fetchUserSurveyResults],
  );

  const fetchUserSurveyResults = useCallback(
    (userId) => graphMutate(
      { t: 'get_survey_results' },
      { survey_slug: surveySlug, user_id: userId, version },
    )
      .then(({ survey_results }) => {
        setPrevContent(
          survey_results[survey_results.length - 1]?.content || undefined,
        );
      })
      .catch((err) => errorHandler('Survey fetchCurrentUser: ', err)),
    [surveySlug, version],
  );

  const createProgressBar = useCallback(
    ({ currentPageNo, title, isShowStartingPage }) => {
      progressBarRef.current = document.createElement('div');
      const progressBar = progressBarRef.current;
      progressBar.setAttribute('id', `progress-bar-${title}`);
      footerRef.current.appendChild(progressBar);
      // when there is a "start" page, hide the progress bar until the "first" real page.
      isShowStartingPage
        ? (progressBar.style.display = 'none')
        : (progressBar.style.cssText = progressBarStyle);

      for (let i = 0; i < pageCountRef.current; i++) {
        const dot = document.createElement('div');
        progressBar.appendChild(dot);
        dot.style.cssText = `border: ${
          i === currentPageNo ? 'var(--hackster)' : 'var(--asphalt)'
        } 2px solid; background-color: ${
          i === currentPageNo ? 'var(--hackster)' : 'var(--asphalt)'
        }; width: 5px;height: 5px; border-radius: 50%;`;
        dot.setAttribute('data-testid', `progress-indicator-${i}`);
      }
    },
    [],
  );

  const updateFooter = useCallback(
    ({ currentPageNo, isGoingBackward, isLastPage }) => {
      // the footer container has a few nested elements we won't show. Change flex layout depending on which page we currently are on.

      // pages are numbered on 0 index
      if (currentPageNo === 1) {
        // set when they progress to the second page
        document.getElementById('sv-nav-start').style.display = 'none';
        document.getElementById('sv-nav-preview').style.display = 'none';
        document.getElementById('sv-nav-complete').style.display = isLastPage
          ? 'block'
          : 'none';
        footerRef.current.style['justify-content'] = 'space-between';
      } else if (currentPageNo === 0) {
        // handle backwards navigation
        footerRef.current.style['justify-content'] = 'end';
      } else if (isGoingBackward && !isLastPage) {
        document.getElementById('sv-nav-complete').style.display = 'none';
      } else if (isLastPage) {
        document.getElementById('sv-nav-complete').style.display = 'block';
      }
    },
    [],
  );

  const updateProgressBar = useCallback(
    ({
      currentPageNo,
      isShowStartingPage,
      shouldRecreateDots,
      surveyTitle,
      visiblePageCount,
    }) => {
      if (shouldRecreateDots) {
        const progressBar = progressBarRef.current;
        progressBar.parentNode.removeChild(progressBar);
        progressBar.current = null;
        pageCountRef.current = visiblePageCount - (isShowStartingPage ? 1 : 0);
        createProgressBar({
          currentPageNo,
          title: surveyTitle,
          isShowStartingPage,
        });
      } else {
        const dots = Array.from(progressBarRef.current.children);
        dots.map((dot, i) => {
          dot.style.cssText = `border: ${
            i === currentPageNo ? 'var(--hackster)' : 'var(--asphalt)'
          } 2px solid; background-color: ${
            i === currentPageNo ? 'var(--hackster)' : 'var(--asphalt)'
          }; width: 5px;height: 5px; border-radius: 50%;`;
        });
      }
    },
    [createProgressBar],
  );

  const handleConvertMarkdown = useCallback((_survey, options) => {
    const md = markdownIt();
    const html = md.render(options.text);
    // Strip out the added paragraph tags `<p>question label with **markdown**?</p>` -> `question label with **markdown**?`. This is needed to keep the required asterisk inline.
    const result = html.slice(3, html.length - 5);
    options.html = result;
  }, []);

  const handleRender = useCallback(
    (survey) => {
      const { visiblePageCount, currentPageNo, isShowStartingPage, title }
        = survey;
      pageCountRef.current = visiblePageCount;
      // the footer container has a few nested elements we won't show. Change flex layout if it is a single page
      const containerEl = document.getElementById(title);
      const footer = containerEl.querySelectorAll('.sd-action-bar');
      footerRef.current = footer[footer.length - 1];

      if (visiblePageCount <= 1 || isShowStartingPage) {
        footerRef.current.style.display = 'flex';
        footerRef.current.style['justify-content'] = 'center';

        if (!isShowStartingPage) return;
      } else {
        footerRef.current.style.display = 'flex';
        footerRef.current.style['justify-content'] = 'end';
      }
      createProgressBar({ currentPageNo, title, isShowStartingPage });
    },
    [createProgressBar],
  );

  const handleStart = useCallback(
    (survey) => {
      // when there is a "start" page, show the progress bar on the "first" real page.
      if (!survey.firstPageIsStarted) {
        return;
      }

      const { title } = survey;

      addToDataLayer({
        event: 'Survey started',
        survey: title,
        version: surveyJson.version,
      });

      footerRef.current.style['justify-content'] = 'end';
      progressBarRef.current.style.cssText = progressBarStyle;
    },
    [surveyJson.version],
  );

  const handlePageChange = useCallback(
    (survey, options) => {
      const {
        currentPage,
        currentPageNo,
        visiblePageCount,
        title,
        isShowStartingPage,
        isLastPage,
      } = survey;
      const { isGoingBackward } = options;

      !isGoingBackward
      && addToDataLayer({
        event: 'Survey page changed',
        survey: title,
        version: surveyJson.version,
        currentPageNo,
        currentPage: currentPage.name,
      });

      const shouldRecreateDots = pageCountRef.current !== visiblePageCount;
      updateFooter({ currentPageNo, isGoingBackward, isLastPage });
      updateProgressBar({
        currentPageNo,
        isShowStartingPage,
        shouldRecreateDots,
        surveyTitle: title,
        visiblePageCount,
      });
    },
    [surveyJson.version, updateFooter, updateProgressBar],
  );

  const surveyComplete = useCallback(
    (survey) => {
      addToDataLayer({
        event: 'Survey submitted',
        survey: survey.title,
        version: surveyJson.version,
      });
      onSubmit({
        content: survey.data,
        status: 'complete',
        isUpdating: Boolean(prevContent),
      });
    },
    [onSubmit, prevContent, surveyJson.version],
  );

  const surveyInComplete = useCallback(
    (survey) => {
      if (Object.keys(survey.data).length === 0) return;

      addToDataLayer({
        event: 'Survey aborted',
        survey: survey.title,
        version: surveyJson.version,
      });

      onSubmit({
        content: survey.data,
        status: 'incomplete',
        isUpdating: Boolean(prevContent),
      });
      // reset survey
      survey.clear(true, true);
    },
    [onSubmit, prevContent, surveyJson.version],
  );

  const handleProcessHtml = useCallback(
    (_survey, options) => {
      // When we exit the survey, ie. it is completed, we want to show the close button. This button is not apart of the survey, but a child of the Popup modal.
      if (element !== 'embed' && options.reason === 'completed') {
        closeButton.current.style.display = 'flex';
        closeButton.current.style['justify-content'] = 'center';
      }
    },
    [element],
  );

  const handleOpenChange = (isOpening) => {
    if (isOpening) {
      onOpenChange?.(isOpening);
      return;
    }
    // send partially completed survey
    if (survey.state !== 'completed' && Object.keys(survey.data).length) {
      onSubmit({
        content: survey.data,
        status: 'incomplete',
        isUpdating: Boolean(prevContent),
      });
    }
    // reset survey and close
    survey.clear(true, true);
    const event = new Event('closeSurvey');
    document.dispatchEvent(event);

    addToDataLayer({
      event: 'Survey dismissed',
      survey: survey.title,
      version: surveyJson.version,
    });

    onOpenChange?.(isOpening);
  };

  const survey = useMemo(() => {
    if (!popup?.open && element !== 'embed') {
      return null;
    }

    const model = new Model(surveyJson);

    model.applyTheme(sjsStyleVars);
    model.css = { ...baseCss, ...surveyCss };

    // set content to anything previously saved
    if (prevContent) {
      model.data = prevContent;
      if (prevContent.pageNo) {
        model.currentPageNo = prevContent.pageNo;
      }
    }

    // replace default icons from surveyJs and use our own
    SvgRegistry.registerIconFromSvg('icon-cancel', closeIcon);
    SvgRegistry.registerIconFromSvg('icon-clear', closeIcon);

    //  use custom component for rating question
    ReactElementFactory.Instance.registerElement('RobotRating', (props) => React.createElement(RobotRating, { ...props, survey: model }),
    );
    // use custom radio select component
    ReactElementFactory.Instance.registerElement('RadioGroup', (props) => React.createElement(RadioGroup, { ...props, survey: model }),
    );

    addToDataLayer({
      event: 'Survey opened',
      survey: model.title,
      version: surveyJson.version,
    });

    // events will fire in this order, with the exception of onProcessHtml
    model.onTextMarkdown.add(handleConvertMarkdown);
    model.onAfterRenderSurvey.add(handleRender);
    model.onStarted.add(handleStart);
    model.onCurrentPageChanged.add(handlePageChange);
    model.onComplete.add(surveyComplete);
    model.onProcessHtml.add(handleProcessHtml); // we only care about this event when it fires after onComplete.

    return model;
  }, [
    element,
    handleConvertMarkdown,
    handlePageChange,
    handleProcessHtml,
    handleRender,
    handleStart,
    popup?.open,
    prevContent,
    surveyComplete,
    surveyCss,
    surveyJson,
  ]);

  useEffect(() => {
    if (allowRepeatEntries) return;
    // if we don't allow multiple entries, we want to grab prefilled data if it exists
    const fetchPreviousData = async () => {
      await fetchUserId();
    };

    fetchPreviousData();
    // only run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // clean up on unmount - send partial data and log aborted survey
  useEffect(
    () => () => {
      if (element === 'embed') {
        surveyInComplete(survey);
      }
    },
    // only run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getElement = () => {
    switch (element) {
      case 'dialog':
        return (
          <Dialog
            asModal={false}
            classList={{
              container: popup.classList?.container,
              trigger: `${button.unset} ${popup.classList?.trigger}`,
            }}
            hasClose={popup.hasClose}
            id="surveyJS"
            onInteractOutside={(e) => e.preventDefault()}
            onOpenChange={handleOpenChange}
            open={popup.open}
            title={popup.title}
            trigger={popup.trigger}
          >
            {popup.open && (
              <div className={c.container} id={surveyJson.title}>
                <SurveyJS model={survey} />
                <div
                  ref={closeButton}
                  data-testid="popup-close-btn"
                  style={{ display: 'none' }}
                >
                  <DialogClose className={c.submitBtn} name="close-survey">
                    Close
                  </DialogClose>
                </div>
              </div>
            )}
          </Dialog>
        );
      case 'popup':
        return (
          <Popup
            align={popup.align || 'start'}
            alignOffset={popup.alignOffset}
            classList={{
              container: popup.classList?.container,
              trigger: `${button.unset} ${popup.classList?.trigger}`,
            }}
            collisionPadding={5}
            disabled={popup.disabled}
            hasClose={popup.hasClose}
            id="surveyJS"
            onOpenChange={handleOpenChange}
            open={popup.open}
            side={popup.side || 'bottom'}
            sideOffset={popup.sideOffset}
            trigger={popup.trigger}
            triggerAria={popup.triggerAria}
          >
            {popup.open && (
              <div className={c.container} id={surveyJson.title}>
                <SurveyJS model={survey} />
                <div
                  ref={closeButton}
                  data-testid="popup-close-btn"
                  style={{ display: 'none' }}
                >
                  <PopupClose className={c.submitBtn} name="close-survey">
                    Close
                  </PopupClose>
                </div>
              </div>
            )}
          </Popup>
        );
      case 'embed':
        return (
          <div id="surveyJS">
            <div id={surveyJson.title}>
              <SurveyJS model={survey} />
            </div>
          </div>
        );
      default:
        throw new Error('Missing element type');
    }
  };

  return getElement();
};

export default SurveyCore;

SurveyCore.propTypes = {
  allowRepeatEntries: PropTypes.bool,
  element: PropTypes.oneOf(['dialog', 'popup', 'embed']).isRequired,
  onOpenChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  popup: PropTypes.shape({
    align: PropTypes.string,
    alignOffset: PropTypes.number,
    classList: PropTypes.shape({
      container: PropTypes.string,
      trigger: PropTypes.string,
    }),
    hasClose: PropTypes.bool,
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    side: PropTypes.string,
    sideOffset: PropTypes.number,
    title: PropTypes.string,
    trigger: PropTypes.node.isRequired,
  }),
  surveyCss: PropTypes.shape({}), // target surveyJS elements directly
  surveyJson: PropTypes.shape({}).isRequired,
  surveySlug: PropTypes.string.isRequired,
  version: PropTypes.number.isRequired,
};
